import React, {
    ReactNode,
    useContext,
    useEffect,
    useState,
    createContext,
    useRef,
    useCallback,
} from 'react';
import { ServerNotification } from '../types/serverNotifications/serverNotificationTypes';
import {
    getInitialServerNotifications,
    resolveAlert,
    resolveAllAlerts,
} from '../services/serverNotifications/serverNotificationsServices';
import { adaptServerNotifications } from '../services/serverNotifications/serverNotificationsAdapter';
import { useNotification } from './NotificationsContext';
import { FeedbackMessages } from '../utils/commonVariables/messages';
import useSignalRConnection from '../customHooks/useSignalRConnection';
import { useActivitySession } from '../customHooks/useActivitySession';
import {
    mergePendingNotifications,
    removeResolvedNotifications,
} from '../utils/serverNotifications/notificationState';

interface ServerNotificationsContextType {
    notifications: ServerNotification[];
    isConnected: boolean;
    isLoading: boolean;
    hasError: boolean;
    getInitialNotifications: () => Promise<void>;
    unresolvedCount: number;
    markAsResolved: (id: string) => Promise<boolean>;
    markAllAsResolved: (ids: string[]) => Promise<boolean>;
    isResolving: boolean;
    subscribeToRefresh: (listener: () => void) => () => void;
}

const ServerNotificationsContext = createContext<
    ServerNotificationsContextType | undefined
>(undefined);

/**
 * Shares pending notifications and confirmed resolutions across authenticated tabs.
 * @param props.children Context consumers.
 * @returns The server notification provider, distinct from feedback toasts.
 */
export const ServerNotificationsProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const session = useActivitySession();
    const { setErrorMessage } = useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isResolving, setIsResolving] = useState(false);
    const [notifications, setNotifications] = useState<ServerNotification[]>(
        []
    );
    const activeSessionRef = useRef<typeof session | null>(session);
    activeSessionRef.current = session;
    const resolvingSessionRef = useRef<typeof session | null>(null);
    const liveUpdatesRef = useRef(new Map<string, ServerNotification>());
    const resolvedIdsRef = useRef(new Set<string>());
    const requestIdRef = useRef(0);
    const loadingRequestRef = useRef<number | null>(null);
    const refreshListenersRef = useRef(new Set<() => void>());
    const subscribeToRefresh = useCallback((listener: () => void) => {
        refreshListenersRef.current.add(listener);
        return () => {
            refreshListenersRef.current.delete(listener);
        };
    }, []);
    const notifyRefresh = () =>
        refreshListenersRef.current.forEach(listener => listener());
    const isCurrentSession = () =>
        session.isAuthenticated &&
        session.isCurrent() &&
        activeSessionRef.current === session;

    const applyResolutions = (ids: string[]) => {
        if (isCurrentSession()) {
            ids.forEach(id => resolvedIdsRef.current.add(id));
            setNotifications(previous =>
                removeResolvedNotifications(previous, new Set(ids))
            );
        }
    };

    const getInitialNotifications = async () => {
        if (!isCurrentSession()) {
            return;
        }
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        loadingRequestRef.current = requestId;
        liveUpdatesRef.current.clear();
        setIsLoading(true);
        setHasError(false);
        try {
            const response = await getInitialServerNotifications();
            if (isCurrentSession() && requestIdRef.current === requestId) {
                setNotifications(
                    mergePendingNotifications(
                        adaptServerNotifications(response.Result),
                        liveUpdatesRef.current,
                        resolvedIdsRef.current
                    )
                );
            }
        } catch {
            if (isCurrentSession() && requestIdRef.current === requestId) {
                setHasError(true);
                setErrorMessage(
                    FeedbackMessages.ERROR_LOADING_INITIAL_NOTIFICATIONS
                );
            }
        } finally {
            if (isCurrentSession() && requestIdRef.current === requestId) {
                loadingRequestRef.current = null;
                liveUpdatesRef.current.clear();
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        activeSessionRef.current = session;
        liveUpdatesRef.current.clear();
        resolvedIdsRef.current.clear();
        loadingRequestRef.current = null;
        setNotifications([]);
        setHasError(false);
        setIsResolving(false);
        setIsLoading(false);
        void getInitialNotifications();
        return () => {
            activeSessionRef.current = null;
        };
    }, [session]);

    const { isConnected, broadcastResolved } = useSignalRConnection(
        alert => {
            if (isCurrentSession()) {
                const [notification] = adaptServerNotifications([alert]);
                if (loadingRequestRef.current !== null) {
                    liveUpdatesRef.current.set(notification.id, notification);
                }
                setNotifications(previous =>
                    mergePendingNotifications(
                        [
                            notification,
                            ...previous.filter(
                                item => item.id !== notification.id
                            ),
                        ],
                        new Map(),
                        resolvedIdsRef.current
                    )
                );
                notifyRefresh();
            }
        },
        () => {
            void getInitialNotifications();
            notifyRefresh();
        },
        applyResolutions
    );

    const resolveNotifications = async (
        ids: string[],
        all: boolean
    ): Promise<boolean> => {
        if (
            !isCurrentSession() ||
            ids.length === 0 ||
            resolvingSessionRef.current === session
        ) {
            return false;
        }
        resolvingSessionRef.current = session;
        setIsResolving(true);
        try {
            const response = all
                ? await resolveAllAlerts(ids)
                : await resolveAlert(ids[0]);
            if (
                !response ||
                response.Success === false ||
                (!all && response.Success !== true)
            ) {
                throw new Error('The server rejected notification resolution');
            }
            if (!isCurrentSession()) {
                return false;
            }
            applyResolutions(ids);
            broadcastResolved(ids);
            return true;
        } catch {
            if (isCurrentSession()) {
                setErrorMessage(FeedbackMessages.ERROR_RESOLVING_NOTIFICATION);
            }
            return false;
        } finally {
            if (resolvingSessionRef.current === session) {
                resolvingSessionRef.current = null;
            }
            if (isCurrentSession()) {
                setIsResolving(false);
            }
        }
    };

    return (
        <ServerNotificationsContext.Provider
            value={{
                notifications,
                isConnected,
                isLoading,
                hasError,
                unresolvedCount: notifications.length,
                getInitialNotifications,
                markAsResolved: id => resolveNotifications([id], false),
                markAllAsResolved: ids => resolveNotifications(ids, true),
                isResolving,
                subscribeToRefresh,
            }}
        >
            {children}
        </ServerNotificationsContext.Provider>
    );
};

/**
 * Reads pending server notifications and their resolution actions.
 * @returns The notification context.
 */
export const useServerNotifications = () => {
    const context = useContext(ServerNotificationsContext);
    if (!context) {
        throw new Error(
            'useServerNotifications must be used within a ServerNotificationsProvider'
        );
    }
    return context;
};
